import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import _ from "lodash/fp";
import {
  DotsVertical as DotsVerticalIcon,
  Plus as PlusIcon,
  Upload as UploadIcon,
  SignLanguage as ASLIcon,
} from "@actinc/dls/icons";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  AppBar,
  Box,
  Button,
  DialogContentText,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useClient, useMutation } from "urql";

import { UsersContext } from "../../../usersProvider";

import { AlertDialog, Backdrop } from "../../../components";

import {
  useMemoCompare,
  useSessionStore,
  useStore,
} from "../../../services/hooks";

import {
  displayFileSize,
  getTasksByObjects,
  getTaskPermissions,
  uploadFileToS3,
} from "../../../services/utils";

import { AssetsDialog } from "./AssetsDialog";
import { assetCardTabs as assetsTabs } from "./constants";
import { CreateArtTaskDialog } from "./CreateArtTaskDialog";
import {
  READY_COMPONENT_TYPE,
  useReadyContext,
} from "../../../providers/ReadyProvider";

const AssetsCard = ({ activeTasks, allObjectTasks, objectType, readonly }) => {
  const cardName = `${_.capitalize(objectType)}AssetsCard`;

  const { getValues } = useFormContext();

  const [workspace, workspaceStore] = useSessionStore("workspace");
  const { selectedTab } = useMemoCompare(
    workspace?.cards.find((c) => c.i === cardName).state || {},
  );

  const [loading, setLoading] = useState(false);

  const [object] = useStore(objectType);
  const { markReady } = useReadyContext();

  useEffect(() => {
    if (objectType === "passage") {
      markReady([], READY_COMPONENT_TYPE.PASSAGE);
    } else if (objectType === "item") {
      markReady([], READY_COMPONENT_TYPE.ITEM);
    }
  }, []);

  const permissions = useMemo(
    () => getTaskPermissions(activeTasks[objectType]),
    [activeTasks[objectType]],
  );

  const tabs = useMemo(
    () =>
      assetsTabs[objectType].filter((assetTab) => {
        return (
          assetTab.label !== "Graphics" ||
          object?._actions.includes("manageGraphicsAssets") ||
          permissions?.includes("graphics")
        );
      }),
    [object?._actions],
  );

  if (!object || !getValues(`${objectType}.id`)) return null;

  return (
    <>
      {object.passthrough ? (
        <Typography>This information is unavailable</Typography>
      ) : (
        <>
          <Backdrop loading={loading} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              width: "100%",
            }}
          >
            <TabContext value={selectedTab}>
              <AppBar color="transparent" elevation={1} position="static">
                <TabList
                  variant="scrollable"
                  onChange={workspaceStore.selectTab(cardName)}
                >
                  {tabs.map(({ label }) => (
                    <Tab key={label} label={label} value={label} />
                  ))}
                </TabList>
              </AppBar>
              {tabs.map(({ label, type, controls }) => (
                <TabPanel
                  key={label}
                  sx={{ overflowY: "auto", padding: 1 }}
                  value={label}
                >
                  <AssetTab
                    activeTask={activeTasks[objectType]}
                    allObjectTasks={allObjectTasks}
                    controlData={controls}
                    object={object}
                    objectType={objectType}
                    permissions={permissions}
                    readonly={readonly}
                    setLoading={setLoading}
                    type={type}
                  />
                </TabPanel>
              ))}
            </TabContext>
          </Box>
        </>
      )}
    </>
  );
};

const AssetTab = ({
  activeTask,
  allObjectTasks,
  controlData,
  object,
  objectType,
  permissions,
  readonly: readonlyProp,
  setLoading,
  type,
}) => {
  // A non-admin Content Specialist can be granted metafile-only access (add,
  // edit, or delete their own newly-uploaded metafiles) on an otherwise
  // readonly object (e.g. Banked) without gaining access to any other tab.
  const isMetafileOnlyAccess =
    type === "metafile" &&
    !object._actions.includes("edit") &&
    object._actions.includes("manageMetafileAssets");
  const readonly = readonlyProp && !isMetafileOnlyAccess;

  const users = useContext(UsersContext);
  const client = useClient();
  const hiddenFileInputRef = useRef(null);

  const [alertDialogProps, setAlertDialogProps] = useState({
    open: false,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [assetDialogProps, setAssetDialogProps] = useState({
    open: false,
  });
  const [createArtTaskDialogProps, setCreateArtTaskDialogProps] = useState({
    open: false,
  });
  const [viewDialogProps, setViewDialogProps] = useState({
    open: false,
    row: null,
  });
  const [currentFileTypes, setCurrentFileTypes] = useState();
  const [disableDelete, setDisableDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [uploadType, setUploadType] = useState();
  const [isCreateArtButtonDisabled, setCreateArtIsDisabled] = useState(false);

  const [createTaskResponse, createTask] = useMutation(createTaskMutation);

  const imageTypes =
    ".jpg, .jpeg, .png, .gif, .tiff, .psd, .pdf, .eps, .ai, .indd, .raw, .svg";

  const { control, getValues, setValue } = useFormContext();
  const {
    fields: rows,
    prepend,
    remove,
    update,
  } = useFieldArray({ control, name: `${objectType}.${type}Assets` });

  // Assets already persisted on the server are "existing"; anything added
  // afterward (this session, via prepend) is new. Identified by name, not
  // react-hook-form's own field id: MetafileAsset has no real id field, so a
  // form reset after a successful save (no page reload) would otherwise
  // assign every row - including already-existing ones - a fresh random key,
  // making them indistinguishable from a brand new upload.
  const originalRowNames = useMemo(
    () => new Set((object[`${type}Assets`] || []).map((asset) => asset.name)),
    [object, type],
  );

  useEffect(() => {
    if (!createTaskResponse?.data?.createTask) return;
    const reloadTasks = async () => {
      const serverTasks = await getTasksByObjects(client)({
        id: object.id,
        type: objectType,
      });
      const formTasks = getValues(`${objectType}.tasks`);
      setValue(
        `${objectType}.tasks`,
        serverTasks.map((task) => {
          if (!task._actions.includes("edit")) return { ...task };
          const formTask = formTasks.find((o) => o.id === task.id);
          return { ...task, ...formTask };
        }),
      );
    };
    reloadTasks();
  }, [createTaskResponse]);

  const settings = {
    graphic: {
      acceptableFileTypes: ".pdf, .indd, .ai, .zip",
      columns: [
        {
          align: "center",
          getValue: () => [
            {
              disabled: readonly,
              label: "Delete",
              onClick: (row) => () => {
                closeActionsMenu();
                setAlertDialogProps({
                  confirmButtonText: "Delete",
                  message: (
                    <DialogContentText>{`Delete graphic asset ${row.name}?`}</DialogContentText>
                  ),
                  onClose: (confirmed) => {
                    setAlertDialogProps({ open: false });
                    if (!confirmed) return;
                    const index = rows.findIndex((o) => o.name === row.name);
                    remove(index);
                  },
                  open: true,
                  dialogTitle: `Delete Asset`,
                });
              },
            },
          ],
          label: "Actions",
        },
        {
          align: "left",
          getValue: (row) => (
            <Box
              sx={{
                whiteSpace: "nowrap",
              }}
            >
              <Tooltip title="Download" aria-label={`Download Graphic Asset`}>
                <Link
                  aria-label={`Download Graphic Asset ${row.name}`}
                  download
                  href={row.src}
                  rel="noreferrer"
                  sx={{ color: "primary.dark" }}
                  target="_blank"
                  underline="none"
                >
                  {row.name}
                </Link>
              </Tooltip>
            </Box>
          ),
          label: "Filename",
        },
        {
          align: "center",
          getValue: (row) => row.fileType,
          label: "File Type",
        },
        {
          align: "right",
          getValue: (row) => displayFileSize(row.fileSize),
          label: "File Size",
        },
        {
          align: "right",
          getValue: (row) =>
            row.fileTimestamp
              ? new Date(row.fileTimestamp).toLocaleString()
              : null,
          label: "Date/Time",
        },
        {
          align: "right",
          getValue: (row) =>
            users.find((element) => element.id === row.user)?.name,
          label: "User",
        },
      ],
      overwrite: true,
      uploadLabel: "Upload Graphic",
    },
    image: {
      acceptableFileTypes: "image/*",
      columns: [
        {
          align: "center",
          getValue: () => [
            {
              disabled: readonly,
              label: "Edit",
              onClick: (row) => () => {
                closeActionsMenu();
                openImageDialog({
                  image: row,
                });
              },
            },
            {
              disabled: readonly,
              label: "Delete",
              onClick: (row) => () => {
                closeActionsMenu();
                setAlertDialogProps({
                  confirmButtonText: "Delete",
                  message: (
                    <DialogContentText>{`Delete image asset ${row.name}?`}</DialogContentText>
                  ),
                  onClose: (confirmed) => {
                    setAlertDialogProps({ open: false });
                    if (!confirmed) return;
                    const index = rows.findIndex((o) => o.name === row.name);
                    remove(index);
                  },
                  open: true,
                  dialogTitle: `Delete Image`,
                });
              },
            },
            {
              disabled: readonly,
              filter: (row) => !!row.printableAsset,
              label: "Overwrite Printable Asset",
              onClick: (row) => () => {
                closeActionsMenu();
                openFileSelector("pbt")(settings[type].printableFileTypes)();
              },
            },
            {
              disabled: readonly,
              filter: (row) => !!row.printableAsset,
              label: "Delete Printable Asset",
              onClick: (row) => () => {
                closeActionsMenu();
                setAlertDialogProps({
                  confirmButtonText: "Delete",

                  message: (
                    <DialogContentText>{`Delete printable asset ${row.printableAsset.name}?`}</DialogContentText>
                  ),
                  onClose: (confirmed) => {
                    setAlertDialogProps({ open: false });
                    if (!confirmed) return;
                    const index = rows.findIndex((o) => o.name === row.name);
                    update(index, { ...row, printableAsset: null });
                  },
                  open: true,
                  dialogTitle: `Delete Asset`,
                });
              },
            },
            {
              disabled: readonly,
              filter: (row) => !row.printableAsset,
              label: "Upload Printable Asset",
              onClick: (row) => () => {
                closeActionsMenu();
                openFileSelector("pbt")(settings[type].printableFileTypes)();
              },
            },
            {
              label: "View",
              onClick: (row) => () => {
                closeActionsMenu();
                setViewDialogProps({
                  open: true,
                  row: row,
                });
              },
            },
          ],
          label: "Actions",
        },
        {
          align: "left",
          getValue: (row) => (
            <>
              <Box sx={{ whiteSpace: "nowrap" }}>
                <Typography component="span" variant="body2">
                  CBT:{" "}
                </Typography>
                <Tooltip title="Download" aria-label={`Download CBT Asset`}>
                  <Link
                    aria-label={`Download CBT Asset ${row.name}`}
                    download
                    href={row.src}
                    rel="noreferrer"
                    sx={{ color: "primary.dark" }}
                    target="_blank"
                    underline="none"
                  >
                    {row.name}
                  </Link>
                </Tooltip>
              </Box>
              {row.printableAsset && (
                <Box sx={{ whiteSpace: "nowrap" }}>
                  <Typography component="span" variant="body2">
                    PBT:{" "}
                  </Typography>
                  <Tooltip title="Download" aria-label="Download PBT Asset">
                    <Link
                      aria-label={`Download PBT Asset ${row.printableAsset.name}`}
                      download
                      href={row.printableAsset.src}
                      rel="noreferrer"
                      sx={{ color: "primary.dark" }}
                      target="_blank"
                      underline="none"
                    >
                      {row.printableAsset.name}
                    </Link>
                  </Tooltip>
                </Box>
              )}
            </>
          ),
          label: "Filename",
        },
        {
          align: "left",
          getValue: (row) => row.identifier,
          label: "Identifier",
        },
        {
          align: "left",
          getValue: (row) => `${row.width ?? "--"} x ${row.height ?? "--"}`,
          label: "Dimensions (px)",
          sx: { whiteSpace: "nowrap" },
        },
        {
          align: "left",
          getValue: (row) => (
            <>
              <Box sx={{ whiteSpace: "nowrap" }}>
                <Typography component="span" variant="body2">
                  CBT: {displayFileSize(row.fileSize)}
                </Typography>
              </Box>
              {row.printableAsset && (
                <Box sx={{ whiteSpace: "nowrap" }}>
                  <Typography component="span" variant="body2">
                    PBT: {displayFileSize(row.printableAsset.fileSize)}
                  </Typography>
                </Box>
              )}
            </>
          ),
          label: "File Size",
          sx: { whiteSpace: "nowrap" },
        },
        {
          align: "left",
          getValue: (row) => (
            <>
              <Box sx={{ whiteSpace: "nowrap" }}>
                <Typography component="span" variant="body2">
                  CBT:{" "}
                  {row.fileTimestamp
                    ? new Date(row.fileTimestamp).toLocaleString()
                    : null}
                </Typography>
              </Box>
              {row.printableAsset && (
                <Box sx={{ whiteSpace: "nowrap" }}>
                  <Typography component="span" variant="body2">
                    PBT:{" "}
                    {row.printableAsset.fileTimestamp
                      ? new Date(
                          row.printableAsset.fileTimestamp,
                        ).toLocaleString()
                      : null}
                  </Typography>
                </Box>
              )}
            </>
          ),
          label: "Date/Time",
          sx: { whiteSpace: "nowrap" },
        },
        {
          align: "left",
          getValue: (row) => (
            <>
              <Box sx={{ whiteSpace: "nowrap" }}>
                <Typography component="span" variant="body2">
                  CBT: {users.find((element) => element.id === row.user)?.name}
                </Typography>
              </Box>
              {row.printableAsset && (
                <Box sx={{ whiteSpace: "nowrap" }}>
                  <Typography component="span" variant="body2">
                    PBT:{" "}
                    {
                      users.find(
                        (element) => element.id === row.printableAsset.user,
                      )?.name
                    }
                  </Typography>
                </Box>
              )}
            </>
          ),
          label: "User",
          sx: { whiteSpace: "nowrap" },
        },
      ],
      overwrite: false,
      printableFileTypes: ".eps, .pdf",
      uploadLabel: "Upload Image",
    },
    media: {
      acceptableFileTypes: ".mp3, .mp4, .m4a, .m4v, .ogg, .webm",
      columns: [
        {
          align: "center",
          getValue: () => [
            {
              disabled: readonly,
              label: "Delete",
              onClick: (row) => () => {
                closeActionsMenu();
                setAlertDialogProps({
                  confirmButtonText: "Delete",
                  message: (
                    <DialogContentText>{`Delete media asset ${row.name}?`}</DialogContentText>
                  ),
                  onClose: (confirmed) => {
                    setAlertDialogProps({ open: false });
                    if (!confirmed) return;
                    const index = rows.findIndex((o) => o.name === row.name);
                    remove(index);
                  },
                  open: true,
                  dialogTitle: `Delete Asset`,
                });
              },
            },
            {
              label: "Preview",
              onClick: (row) => () => {
                closeActionsMenu();
                setAlertDialogProps({
                  maxWidth: "md",
                  onClose: () => {
                    setAlertDialogProps({
                      open: false,
                    });
                  },
                  open: true,
                  dialogTitle: "Preview Media",
                  customFooter: (
                    <>
                      <Button
                        autoFocus
                        color="primary"
                        onClick={() =>
                          setAlertDialogProps({
                            open: false,
                          })
                        }
                        variant="text"
                      >
                        Close
                      </Button>
                    </>
                  ),
                  message: (
                    <>
                      <div>
                        Filename <a href={row?.src}>{row?.name}</a>
                      </div>
                      {["MP3", "M4A", "OGG"].includes(row?.fileType) ? (
                        <audio
                          controls
                          src={row?.src}
                          type={`audio/${row?.fileType}`}
                        >
                          Your browser does not support the audio tag.
                        </audio>
                      ) : (
                        <video
                          aria-label={`${row?.name} media player`}
                          width="100%"
                          height="100%"
                          controls
                        >
                          <source
                            src={row?.src}
                            type={`video/${
                              row?.fileType === "M4V" ? "MP4" : row?.fileType
                            }`}
                          />
                          Your browser does not support the video tag.
                        </video>
                      )}
                    </>
                  ),
                });
              },
            },
          ],
          label: "Actions",
        },
        {
          align: "left",
          getValue: (row) => (
            <Box aria-label={row.name} sx={{ whiteSpace: "nowrap" }}>
              <Tooltip title="Download" aria-label={`Download Media Asset`}>
                <Link
                  aria-label={`Download Media Asset ${row.name}`}
                  download
                  href={row.src}
                  rel="noreferrer"
                  sx={{ color: "primary.dark" }}
                  target="_blank"
                  underline="none"
                >
                  {row.name}
                </Link>
              </Tooltip>
            </Box>
          ),
          label: "Filename",
        },
        {
          align: "left",
          getValue: (row) => row.description,
          label: "Description",
        },
        {
          align: "left",
          getValue: (row) => displayFileSize(row.fileSize),
          label: "File Size",
        },
        {
          align: "left",
          getValue: (row) =>
            row.fileTimestamp
              ? new Date(row.fileTimestamp).toLocaleString()
              : null,
          label: "Date/Time",
        },
        {
          align: "left",
          getValue: (row) =>
            users.find((element) => element.id === row.user)?.name,
          label: "User",
        },
      ],
      overwrite: true,
      uploadLabel: "Upload Media",
    },
    metafile: {
      acceptableFileTypes:
        ".csv, .doc, .docx, .eps, .gif, .html, .jpg, .mp3, .mp4, .pdf, .png, .svg, .txt, .vtt, .webm, .xls, .xlsx, .zip",
      columns: [
        {
          align: "center",
          getValue: (currentRow) => {
            const disabled =
              readonly ||
              (isMetafileOnlyAccess && originalRowNames.has(currentRow.name));
            return [
              {
                disabled,
                label: "Edit",
                onClick: (row) => () => {
                  closeActionsMenu();
                  openMetafilesDialog({
                    metafile: row,
                  });
                },
              },
              {
                disabled,
                label: "Delete",
                onClick: (row) => () => {
                  closeActionsMenu();
                  setAlertDialogProps({
                    confirmButtonText: "Delete",
                    message: (
                      <DialogContentText>{`Delete metafile asset ${row.name}?`}</DialogContentText>
                    ),
                    onClose: (confirmed) => {
                      setAlertDialogProps({ open: false });
                      if (!confirmed) return;
                      const index = rows.findIndex((o) => o.name === row.name);
                      remove(index);
                    },
                    open: true,
                    dialogTitle: `Delete Asset`,
                  });
                },
              },
            ];
          },
          label: "Actions",
        },
        {
          align: "left",
          getValue: (row) => (
            <Box sx={{ whiteSpace: "nowrap" }}>
              <Tooltip title="Download" aria-label={`Download Metafile Asset`}>
                <Link
                  aria-label={`Download Metafile Asset ${row.name}`}
                  download
                  href={row.src}
                  rel="noreferrer"
                  sx={{ color: "primary.dark" }}
                  target="_blank"
                  underline="none"
                >
                  {row.name} {row.isASL && <ASLIcon fontSize="small" />}
                </Link>
              </Tooltip>
            </Box>
          ),
          label: "Filename",
        },
        {
          align: "left",
          getValue: (row) => row.description,
          label: "Description",
        },
        {
          align: "left",
          getValue: (row) => displayFileSize(row.fileSize),
          label: "File Size",
        },
        {
          align: "left",
          getValue: (row) =>
            row.fileTimestamp
              ? new Date(row.fileTimestamp).toLocaleString()
              : null,
          label: "Date/Time",
        },
        {
          align: "left",
          getValue: (row) =>
            users.find((element) => element.id === row.user)?.name,
          label: "User",
        },
      ],
      overwrite: true,
      uploadLabel: "Upload Metafile",
    },
    "program.metafile": {
      columns: [
        {
          align: "center",
          disabled: (row) =>
            [
              "csv",
              "doc",
              "docx",
              "html",
              "mp3",
              "mp4",
              "txt",
              "xls",
              "xlsx",
              "zip",
            ].includes(row?.fileType?.toLowerCase()),
          getValue: (row) => {
            return [
              {
                label: "Preview",
                onClick: (row) => () => {
                  closeActionsMenu();
                  setAlertDialogProps({
                    maxWidth: "md",
                    onClose: () => {
                      setAlertDialogProps({
                        open: false,
                      });
                    },
                    open: true,
                    dialogTitle: "Preview Program Metadata Asset",
                    customFooter: (
                      <>
                        <Button
                          autoFocus
                          color="primary"
                          onClick={() =>
                            setAlertDialogProps({
                              open: false,
                            })
                          }
                          variant="text"
                        >
                          Close
                        </Button>
                      </>
                    ),
                    message: (
                      <>
                        <div>Filename {row.name}</div>
                        <img alt={row.name} src={row?.src} />
                      </>
                    ),
                  });
                },
              },
            ];
          },
          label: "Actions",
        },
        {
          align: "left",
          getValue: (row) => (
            <Box aria-label={row.name} sx={{ whiteSpace: "nowrap" }}>
              <Tooltip
                title="Download"
                aria-label={`Download Program Metafile Asset`}
              >
                <Link
                  aria-label={`Download Program Metafile Asset ${row.name}`}
                  download
                  href={row.src}
                  rel="noreferrer"
                  sx={{ color: "primary.dark" }}
                  target="_blank"
                  underline="none"
                >
                  {row.name}
                </Link>
              </Tooltip>
            </Box>
          ),
          label: "Filename",
        },
        {
          align: "left",
          getValue: (row) => row.comment,
          label: "Comment",
        },
        {
          align: "left",
          getValue: (row) => row.category,
          label: "Category",
        },
      ],
    },
  };

  const handleAssetUpload = async ({ file, ...formVals } = {}) => {
    const { size: fileSize } = file;
    const filetype = file?.name.substring(
      file?.name.lastIndexOf(".", file?.name.length),
    );
    let validFileType = false;

    if (currentFileTypes === "image/*") {
      validFileType = imageTypes.includes(filetype.toLowerCase());
    } else {
      validFileType = currentFileTypes.includes(filetype.toLowerCase());
    }

    if (!validFileType) {
      // display alert
      setAlertDialogProps({
        confirmButtonText: "OK",
        message: (
          <DialogContentText>{`Invalid file type: "${filetype}"`}</DialogContentText>
        ),
        onClose: (confirmed) => {
          setAlertDialogProps({ open: false });
        },
        open: true,
        dialogTitle: `Invalid File Type`,
      });
      return;
    }

    setLoading(true);
    const { name } = await uploadFileToS3(client)({
      addTimestampToName: !["graphic", "metafile"].includes(type),
      file,
    });
    const asset = {
      ...formVals,
      fileSize,
      name,
      src: `/asset_uploads/${name}`,
    };

    const fileType = name.split(".").pop().toUpperCase();
    const index = selectedRow
      ? rows.findIndex((o) => o.name === selectedRow.name)
      : -1;
    switch (uploadType) {
      case "image":
        if (index === -1) {
          prepend({
            ...asset,
          });
        } else {
          update(index, {
            ...selectedRow,
            ...asset,
          });
        }
        break;
      case "graphic":
      case "media":
      case "metafile":
        prepend({ ...asset, fileType });
        break;
      case "pbt":
        update(index, {
          ...selectedRow,
          printableAsset: { ...asset, fileType },
        });
        break;
      default:
    }
    setLoading(false);
    setSelectedRow(null);
  };

  const isLockedExistingMetafile = (assets) => (index) =>
    index > -1 &&
    isMetafileOnlyAccess &&
    originalRowNames.has(assets[index].name);

  const openCannotOverwriteMetafileDialog = (fileName) => {
    setAlertDialogProps({
      confirmButtonText: "OK",
      message: (
        <DialogContentText>{`A metafile with the name ${fileName} already exists and cannot be replaced.`}</DialogContentText>
      ),
      onClose: () => setAlertDialogProps({ open: false }),
      open: true,
      dialogTitle: "Cannot Overwrite Metafile",
    });
  };

  const handleFileChange =
    ({ overwrite, rows: assets }) =>
    (e) => {
      if (!e.target.files.length) return;
      const file = e.target.files[0];

      const index = assets.findIndex((o) => o.name === file.name);

      if (isLockedExistingMetafile(assets)(index)) {
        openCannotOverwriteMetafileDialog(file.name);
        e.target.value = ""; // reset input value
        return;
      }

      if (overwrite && index > -1) {
        setAlertDialogProps({
          confirmButtonText: "Overwrite",

          message: (
            <DialogContentText>{`A file with the name ${file.name} already exists. Overwrite the file?`}</DialogContentText>
          ),
          onClose: (confirmed) => {
            setAlertDialogProps({ open: false });
            if (confirmed) {
              remove(index);
              handleAssetUpload({ file });
            }
          },
          open: true,
          dialogTitle: "Overwrite Asset",
        });
      } else {
        switch (uploadType) {
          case "image":
            openImageDialog({
              file,
              image: assets[index],
            });
            break;
          case "metafile":
            openMetafilesDialog({
              file,
              metafile: assets[index],
            });
            break;
          case "media":
            openMediafilesDialog({
              file,
            });
            break;
          case "graphic":
          case "pbt":
            handleAssetUpload({ file });
            break;
          default:
        }
      }
      e.target.value = ""; // reset input value
    };

  const openActionsMenu = (row) => (e) => {
    setSelectedRow(row);
    setDisableDelete(
      isAssetUsed(row)(object) ||
        (row.__typename === "GraphicAsset" &&
          !(
            object._actions.includes("manageGraphicsAssets") ||
            permissions?.includes("graphics")
          )),
    );
    setAnchorEl(e.target);
  };
  const closeActionsMenu = () => {
    setAnchorEl(null);
  };

  const isAssetUsed = (selectedRow) => (object) => {
    let formObject = getValues(objectType);
    const isSrcInFoils = object?.interactions?.some((interaction) =>
      interaction.foils?.some((foil) =>
        foil.content.includes(`src="${selectedRow?.src}"`),
      ),
    );
    const isSrcInFormObjectFoils = formObject?.interactions?.some(
      (interaction) =>
        interaction.foils?.some((foil) =>
          foil.content.includes(`src="${selectedRow?.src}"`),
        ),
    );
    return (
      isSrcInFoils ||
      isSrcInFormObjectFoils ||
      object?.content?.includes(`src="${selectedRow?.src}"`) ||
      formObject?.content?.includes(`src="${selectedRow?.src}"`)
    );
  };

  const openCreateArtTaskDialog = async () => {
    setCreateArtTaskDialogProps({
      assets: rows,
      object,
      objectType,
      onClose: async (imageDetails) => {
        setCreateArtTaskDialogProps({ open: false });
        if (!imageDetails) return;
        console.log("Create Art Task Dialog return imageDetails", imageDetails);
        try {
          const result = await createTask({
            id: object.id,
            imageDetails,
            taskDefinition: "Art",
            type: objectType,
          });

          if (result?.data?.createTask?.length > 0) {
            setCreateArtIsDisabled(true);
          }
        } catch (error) {
          console.error("Create Task Request failed:", error);
        }
      },
      open: true,
    });
  };

  const openFileSelector = (t) => (acceptableFileTypes) => (e) => {
    setUploadType(t);
    setCurrentFileTypes(acceptableFileTypes);
    hiddenFileInputRef.current.accept = acceptableFileTypes;
    hiddenFileInputRef.current.click();
  };

  const openImageDialog = ({ file, image }) => {
    // we pass the *image type to the file uploader, the following
    // list is what I found online that should be equivalent
    const filetype = file?.name.substring(
      file?.name.lastIndexOf(".", file?.name.length),
    );
    const validFileType = file
      ? imageTypes.includes(filetype?.toLowerCase())
      : true;

    setAssetDialogProps({
      alertProps: {
        showAlert: !validFileType,
        title: `Invalid file type: "${filetype}"`,
        severity: "error",
      },
      assetType: "image",
      btnText: `${image ? "Update" : "Upload"}`,
      controlData,
      defaultValues: {
        ...image,
        altText: image?.altText
          ? image.altText
          : ["Math", "Science"].includes(object.contentArea)
            ? "Refer to the figure in the braille book."
            : "",
      },
      dialogTitle: `${image ? "Update" : "Upload"} Image`,
      disabled: !validFileType,
      image,
      onClose: (asset) => {
        setAssetDialogProps({ open: false });
        if (!asset) return;
        if (file) {
          handleAssetUpload({
            ...asset,
            file,
          });
        } else {
          const index = rows.findIndex((o) => o.name === image.name);
          update(index, { ...image, ...asset });
        }
      },
      objectId: object.id,
      open: true,
      src: file ? URL.createObjectURL(file) : image?.src,
    });
  };

  const openMetafilesDialog = ({ file, metafile }) => {
    const fileSizeGreaterThan50MB = file?.size / 1024 ** 2 > 50;
    const filetype = file?.name.substring(
      file?.name.lastIndexOf(".", file?.name.length),
    );
    const validFileType = file
      ? currentFileTypes.includes(filetype?.toLowerCase())
      : true;

    setAssetDialogProps({
      alertProps: {
        showAlert: fileSizeGreaterThan50MB || !validFileType,
        title: fileSizeGreaterThan50MB
          ? "The filesize is greater than 50 MB"
          : `Invalid file type: "${filetype}"`,
        severity: "error",
      },
      btnText: `${metafile ? "Update" : "Upload"}`,
      controlData,
      dialogTitle: `${metafile ? "Update" : "Upload"} Metafile`,
      disabled: fileSizeGreaterThan50MB || !validFileType,
      maxWidth: "md",
      defaultValues: {
        ...metafile,
      },
      onClose: (asset) => {
        setAssetDialogProps({ open: false });
        if (!asset) return;
        if (file) {
          handleAssetUpload({ ...asset, file });
        } else {
          const index = rows.findIndex((o) => o.name === metafile.name);
          update(index, { ...metafile, ...asset });
        }
      },
      open: true,
    });
  };

  const openMediafilesDialog = ({ file }) => {
    const fileSizeGreaterThan50MB = file?.size / 1024 ** 2 > 50;
    const filetype = file?.name.substring(
      file?.name.lastIndexOf(".", file?.name.length),
    );
    const validFileType = currentFileTypes.includes(filetype.toLowerCase());

    setAssetDialogProps({
      alertProps: {
        showAlert: fileSizeGreaterThan50MB || !validFileType,
        title: fileSizeGreaterThan50MB
          ? "The filesize is greater than 50 MB"
          : `Invalid file type: "${filetype}"`,
        severity: "error",
      },
      btnText: "Upload",
      controlData,
      dialogTitle: "Upload Media",
      disabled: fileSizeGreaterThan50MB || !validFileType,
      maxWidth: "md",
      onClose: (asset) => {
        setAssetDialogProps({ open: false });
        if (!asset) return;
        if (file) {
          handleAssetUpload({ ...asset, file });
        }
      },
      open: true,
    });
  };

  const isInitialCreateArtTaskButtonDisabled = allObjectTasks.some(
    (task) => task.name === "Create Art" && task.completedDateTime === null,
  );

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "end", marginBottom: 1 }}>
        {!readonly &&
          type === "image" &&
          activeTask?._actions.includes("createOptionalTasks") && (
            <Button
              data-testid="create-art-button"
              onClick={openCreateArtTaskDialog}
              size="small"
              startIcon={<PlusIcon />}
              variant="outlined"
              disabled={
                isCreateArtButtonDisabled ||
                isInitialCreateArtTaskButtonDisabled
              }
            >
              Create Art Task
            </Button>
          )}
        {settings[type].uploadLabel &&
          !readonly &&
          ((type === "graphic" &&
            (object._actions.includes("manageGraphicsAssets") ||
              permissions?.includes("graphics"))) ||
            (type === "image" &&
              object._actions.includes("manageImageAssets")) ||
            (type === "media" &&
              object._actions.includes("manageMediaAssets")) ||
            (type === "metafile" &&
              object._actions.includes("manageMetafileAssets"))) && (
            <Button
              onClick={openFileSelector(type)(
                settings[type].acceptableFileTypes,
              )}
              size="small"
              startIcon={<UploadIcon />}
              sx={{ marginLeft: 1 }}
              variant="outlined"
            >
              {settings[type].uploadLabel}
            </Button>
          )}
      </Box>
      <input
        data-testid={`${type}-file-input`}
        onChange={handleFileChange({
          overwrite: settings[type].overwrite,
          rows,
        })}
        ref={hiddenFileInputRef}
        style={{ display: "none" }}
        type="file"
      ></input>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {settings[type].columns.map(
                ({ align = "left", className, label }) => (
                  <TableCell
                    align={align}
                    className={className}
                    key={label}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {label}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.name}>
                {settings[type].columns.map(
                  ({
                    align = "left",
                    className,
                    disabled = () => false,
                    getValue,
                    label,
                  }) => {
                    return label === "Actions" ? (
                      <TableCell
                        align={align}
                        className={className}
                        key={label}
                      >
                        <IconButton
                          aria-haspopup="menu"
                          aria-label={`Open actions menu for ${type} file ${
                            index + 1
                          }`}
                          color="primary"
                          disabled={disabled(row)}
                          onClick={openActionsMenu(row)}
                          size="small"
                        >
                          <DotsVerticalIcon />
                        </IconButton>
                      </TableCell>
                    ) : (
                      <TableCell
                        align={align}
                        className={className}
                        key={label}
                      >
                        {getValue(row)}
                      </TableCell>
                    );
                  },
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Menu
        anchorEl={anchorEl}
        onClose={closeActionsMenu}
        open={Boolean(selectedRow) && Boolean(anchorEl)}
      >
        {!!selectedRow &&
          settings[type].columns
            .find(({ label }) => label === "Actions")
            .getValue(selectedRow)
            .filter(({ filter }) => !filter || filter(selectedRow))
            .map((option) => (
              <MenuItem
                aria-label={option.label}
                dense={true}
                disabled={
                  option.disabled ||
                  (option.label === "Delete" && disableDelete)
                }
                key={option.label}
                onClick={option.onClick(selectedRow)}
              >
                {option.label}
              </MenuItem>
            ))}
      </Menu>
      {alertDialogProps.open && <AlertDialog {...alertDialogProps} />}
      {assetDialogProps.open && <AssetsDialog {...assetDialogProps} />}
      {createArtTaskDialogProps.open && (
        <CreateArtTaskDialog {...createArtTaskDialogProps} />
      )}
      <Dialog
        open={viewDialogProps.open}
        onClose={() => setViewDialogProps({ open: false, row: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>View Image Asset</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box>
              {viewDialogProps.row?.src && (
                <img
                  src={viewDialogProps.row.src}
                  alt={viewDialogProps.row.name}
                />
              )}
            </Box>
            <TextField
              label="Identifier"
              value={viewDialogProps.row?.identifier || ""}
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Alt Text"
              value={viewDialogProps.row?.altText || ""}
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Long Description"
              value={viewDialogProps.row?.longDescription || ""}
              fullWidth
              multiline
              InputProps={{ readOnly: true }}
            />
          </Box>
        </DialogContent>
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={() => setViewDialogProps({ open: false, row: null })}
            color="primary"
            variant="contained"
          >
            Close
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

const createTaskMutation = `
  mutation CreateTask($description: String, $dueDate: AWSDate, $id: ID!, $imageDetails: [TaskImageComponentInput!], $taskDefinition: String!, $type: String!) {
    createTask(description: $description, dueDate: $dueDate, id: $id, imageDetails: $imageDetails, taskDefinition: $taskDefinition, type: $type) {
      id
    }
  }`;

export { AssetsCard };
